import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, ClickAwayListener, IconButton, Typography } from '@mui/material';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { checkUserHasProvidedEmail } from '@src/services/email';
import { COUNTRY, LANGUAGE } from '@src/constants';
import BadgeAvatar from '@src/components/BadgeAvatar';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import CustomTypography from '@src/new-components/Typography';
import { BREAKPOINTS } from '@src/styles/config';
import { useTranslation } from 'react-i18next';
import useCustomSSO from '@src/hooks/useCustomSSO';
import { COLOR } from '@src/styles/color';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import UserActions from './UserActions';
import {
  StyledPopper,
  StyledSidebarTooltip,
  StyledUserSidebarNew,
} from './index.style';
import DrawNotification from './DrawNotification';

export const languages = [
  {
    value: LANGUAGE.VN,
    country: COUNTRY.VN,
    label: 'vietnamese',
    icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/round/vn.png',
  },
  {
    value: LANGUAGE.EN,
    country: COUNTRY.EN,
    label: 'english',
    icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/round/gb.png',
  },
];

const UserInfo = ({
  handleCloseSidebar,
  ipLocation,
  smallSidebar = false,
  isMobile,
}) => {
  const { getFeatureValue } = useFeatureFlags();
  const [openActionsAnchor, setOpenActionsAnchor] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const hasNewNotification = useSelector(
    (state) => state.notification.hasNewNotification,
  );
  const { t } = useTranslation();
  const [openNotification, setOpenNotification] = useState(false);

  const notificationEnabled = getFeatureValue(FEATURE_KEYS.NOTIFICATION, {
    userId: user.id,
    email: user.email,
  });

  const hasProvidedEmail = checkUserHasProvidedEmail(user);

  const { keycloak } = useCustomSSO();

  const handleAvatarClick = (e) => setOpenActionsAnchor(e.currentTarget);

  const checkWindowWidth = () => {
    if (window.innerWidth <= BREAKPOINTS.SM) {
      setOpenActionsAnchor(null);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', checkWindowWidth);
    return () => window.removeEventListener('resize', checkWindowWidth);
  }, []);

  const renderBadgeAvatarIcon = useCallback(() => {
    if (hasProvidedEmail) {
      return null;
    }

    return (
      <div className="badge-container">
        <ErrorRoundedIcon sx={{ color: COLOR.orangeV2[100], fontSize: 20 }} />
      </div>
    );
  }, [hasProvidedEmail]);

  const renderVerifiedStatus = useCallback(() => {
    if (hasProvidedEmail) {
      return (
        <CustomTypography
          variant="body-12-regular"
          color={COLOR.label2[64]}
          className="verified-status"
        >
          {user?.email}
        </CustomTypography>
      );
    }

    return (
      <CustomTypography
        variant="body-12-regular"
        color={COLOR.label2[64]}
        className="verified-status"
      >
        {t('hasNoEmail')}
      </CustomTypography>
    );
  }, [hasProvidedEmail]);

  const renderUserInfo = useCallback(
    () => (
      <div className="user-info-wrapper">
        <Typography className="name">{user.firstName}</Typography>
        {renderVerifiedStatus()}
      </div>
    ),
    [user.firstName, renderVerifiedStatus],
  );

  if (isMobile) {
    return (
      <StyledUserSidebarNew padding="0px">
        {notificationEnabled && (
          <div style={{ position: 'relative' }}>
            <NotificationsRoundedIcon
              onClick={(e) => {
                e.stopPropagation();
                setOpenNotification(true);
              }}
            />
            {hasNewNotification && (
              <span
                style={{
                  position: 'absolute',
                  top: '3px',
                  right: '0px',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: COLOR.functionalRed.default,
                  border: '1.5px solid white',
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
        )}
        <IconButton className="avatar-wrapper">
          <div className="avatar">
            <BadgeAvatar
              img={user.avatar}
              active={keycloak && keycloak.authenticated}
              name={user.firstName || user.lastName}
              number={new Date(user.createdAt)}
              icon={renderBadgeAvatarIcon()}
              smallImgWidth={20}
              type="icon"
              removeDefaultIcon
            />
          </div>
        </IconButton>
        <DrawNotification
          open={openNotification}
          onClose={() => setOpenNotification(false)}
        />
      </StyledUserSidebarNew>
    );
  }

  return (
    <>
      {smallSidebar && (
        <StyledSidebarTooltip notShowTooltip title={null}>
          <StyledUserSidebarNew padding="0px">
            <IconButton className="avatar-wrapper" onClick={handleAvatarClick}>
              <div className="avatar">
                <BadgeAvatar
                  img={user.avatar}
                  active={keycloak && keycloak.authenticated}
                  name={user.firstName || user.lastName}
                  number={new Date(user.createdAt)}
                  icon={renderBadgeAvatarIcon()}
                  smallImgWidth={20}
                  type="icon"
                  removeDefaultIcon
                />
              </div>
            </IconButton>
          </StyledUserSidebarNew>
        </StyledSidebarTooltip>
      )}
      {!smallSidebar && (
        <StyledUserSidebarNew onClick={handleAvatarClick}>
          <div className="avatar-container">
            <IconButton className="avatar-wrapper">
              <div className="avatar">
                <BadgeAvatar
                  img={user?.avatar}
                  active={keycloak && keycloak.authenticated}
                  name={user.firstName || user.lastName}
                  number={new Date(user.createdAt)}
                  icon={renderBadgeAvatarIcon()}
                  smallImgWidth={20}
                  type="icon"
                  removeDefaultIcon
                />
              </div>
            </IconButton>
          </div>
          <Box className="user-info">
            {/* Although we have notShowTooltip prop, we can't use it since making it true will produce some
            weird behavior when user hover in */}
            {`${user?.lastName || ''} ${user?.firstName || ''}`.trim()
              .length === 0 ? (
              renderUserInfo()
            ) : (
              <StyledSidebarTooltip
                title={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <CustomTypography
                      variant="body-14-regular"
                      color={COLOR.whiteV2.default}
                    >
                      {`${user.lastName} ${user.firstName}`}
                    </CustomTypography>
                    {hasProvidedEmail && (
                      <CustomTypography
                        variant="body-14-regular"
                        color={COLOR.whiteV2.default}
                      >
                        {user?.email}
                      </CustomTypography>
                    )}
                  </Box>
                }
                placement="right"
              >
                {renderUserInfo()}
              </StyledSidebarTooltip>
            )}
          </Box>
          <Box className="close-sidebar">
            <StyledSidebarTooltip title={t('collapse')} placement="right">
              <IconButton onClick={handleCloseSidebar}>
                <FirstPageIcon />
              </IconButton>
            </StyledSidebarTooltip>
          </Box>
        </StyledUserSidebarNew>
      )}

      {openActionsAnchor && (
        <ClickAwayListener onClickAway={() => setOpenActionsAnchor(null)}>
          <StyledPopper
            open={Boolean(openActionsAnchor)}
            placement="top-end"
            anchorEl={openActionsAnchor}
            onClose={() => setOpenActionsAnchor(null)}
            modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
            onClick={() => setOpenActionsAnchor(null)}
          >
            <UserActions location={ipLocation} />
          </StyledPopper>
        </ClickAwayListener>
      )}
    </>
  );
};

export default UserInfo;
